import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { BackendService } from '../../../services/backend';
import { ScanTag } from '../../../model';
import { FloatingWindowComponent } from '../floating-window/floating-window.component';
import { problemDetail } from '../tags/tag-rules';
import { TagCloudComponent } from '../tag-cloud/tag-cloud.component';
import { TagsCacheService } from '../tags/tags-cache.service';

/**
 * Movable, non-modal window to change the tag set of one scan: a tag cloud over ALL tags (from the tag cache)
 * with the scan's current tags pre-selected. Save PUTs the selection, emits `saved` with the new ScanTag[] and
 * closes. Cancel / X / Esc discard (ignored while the save is running).
 */
@Component({
  selector: 'app-scan-tags-editor-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FloatingWindowComponent, TagCloudComponent, NzButtonModule, NzSpinModule, NzAlertModule],
  template: `
    <app-floating-window [title]="windowTitle" (closed)="cancel()">
      @if (cache.loading()) {
        <nz-spin nzSimple></nz-spin>
      } @else if (cache.error()) {
        <nz-alert nzType="error" [nzMessage]="cache.error()" nzShowIcon></nz-alert>
        <button nz-button nzSize="small" type="button" class="retry" (click)="cache.ensureLoaded()" i18n>Retry</button>
      } @else {
        <app-tag-cloud [tags]="cache.tags()" [(selectedIds)]="selectedIds" [originalIds]="originalIds()"></app-tag-cloud>
      }
      @if (saveError()) {
        <nz-alert class="save-error" nzType="error" [nzMessage]="saveError()" nzShowIcon></nz-alert>
      }
      <div class="actions">
        <button nz-button type="button" class="cancel-btn" [disabled]="saving()" (click)="cancel()" i18n>Cancel</button>
        <button nz-button nzType="primary" type="button" class="save-btn" [nzLoading]="saving()" [disabled]="!dirty() || saving()" (click)="save()" i18n
          >Save</button
        >
      </div>
    </app-floating-window>
  `,
  styles: [
    `
      .actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 12px;
      }
      .retry,
      .save-error {
        margin-top: 8px;
        display: block;
      }
    `,
  ],
})
export class ScanTagsEditorWindowComponent {
  protected readonly cache = inject(TagsCacheService);
  private readonly backend = inject(BackendService);

  readonly scanId = input.required<string>();
  /** The tags currently saved on the scan (pre-selected). */
  readonly currentTags = input<ScanTag[]>([]);

  /** Emits the scan's new tag set after a successful save (right before `closed`). */
  readonly saved = output<ScanTag[]>();
  readonly closed = output<void>();

  protected readonly windowTitle = $localize`Edit scan tags`;
  protected readonly originalIds = computed(() => this.currentTags().map((scanTag) => scanTag.tagId));
  protected readonly selectedIds = linkedSignal<string[]>(() => this.originalIds());
  protected readonly saving = signal(false);
  protected readonly saveError = signal<string | null>(null);

  protected readonly dirty = computed(() => {
    const original = new Set(this.originalIds());
    const selected = this.selectedIds();
    return selected.length !== original.size || selected.some((tagId) => !original.has(tagId));
  });

  constructor() {
    this.cache.ensureLoaded();
  }

  protected save(): void {
    if (this.saving()) return;
    this.saving.set(true);
    this.saveError.set(null);
    this.backend.setScanTags(this.scanId(), this.selectedIds()).subscribe({
      next: (scanTags) => {
        this.saving.set(false);
        this.saved.emit(scanTags);
        this.closed.emit();
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(problemDetail(err, $localize`Saving the tags failed`));
      },
    });
  }

  protected cancel(): void {
    // Not while saving: a destroyed editor would drop the `saved` event, so the list would keep showing the old tags
    // and the next edit would start from them (an added tag would then be removed without the confirmation).
    if (this.saving()) return;
    this.closed.emit();
  }
}
